// ============================================================================
// Stage 4 pricing on Quote Brain projects (docs/quote-brain/DESIGN.md 2.12 W4
// and 4.2; slice WRITE).
// ----------------------------------------------------------------------------
// A brain row's amount is pricing-core's (price_items on the server). Stage 4
// prices brain projects with the same pricing-core code in the browser, never
// with the old cost engines: the rows carry their build-up under `quoteBrain`
// and no costBreakdown, so cost-math has nothing to re-derive.
//
// A reprice uses the run's own prices: the rate of each build-up line as the
// run recorded it in the row's receipt, and the business's Pricing page
// method the run priced with (quote_data.meta.quoteBrain.pricingMethodology).
// The run read both from the Pricing page; a reprice never reads it again, so
// it moves only what the list change moves (a user without Pricing access
// sees the page under other row ids anyway).
//
// When the list is exactly what the brain wrote (its fingerprint matches),
// nothing is priced again: the server already did. After the builder changes
// the list (removes an item, adds one, edits one), pricing-core prices the
// brain items again with firstRun false, so the admin fee and minimum
// attendance of each approval group follow the items that are left:
//   - a row whose amount the builder set (it no longer equals its pricing
//     receipt) keeps that amount;
//   - statutory rows keep their amounts: after the first run only the
//     builder's Add or Remove HBCF changes them (memory feedback_hbcf_first_run_only).
//
// A quote QuoteMaker did not write (by hand, or before QuoteMaker) has no
// recorded method. An item the builder gives lines (quote-brain-buildup.ts)
// is priced with the business's Pricing page method as it is at the press,
// passed in as `method`, and only on the press: nothing reprices it by itself.
// The job's other items keep their prices but count in the approval group, so
// the admin fee tier, each item's share of it and the minimum attendance are
// the whole job's, as on a QuoteMaker quote. No clean-subtotal nudge: the
// builder's own prices set that total.
// ============================================================================

import { createPriceItems } from '../../backend/src/shared/quote-brain/price-items.js';
import { rateBookFromReceipts, pricingPageMethodology, receiptKey } from '../../backend/src/shared/quote-brain/rate-book-core.js';
import { isManagedComplianceRow, isManagedHbcfRow } from './rj-bird-pricing-methodology';
import { convertScopePricingDisplayBasis, restampScopeItemPricingBasis } from '../utils/inclusions-helpers';
import { isQuoteBrainQuoteData } from '../utils/quote-brain-project';

type Row = Record<string, any>;

/**
 * The pricing options a server run prices with: pricingOptions(SERVER_CONFIG)
 * (backend/src/shared/quote-brain/handlers.js, quote-brain-job/engine.js).
 * Spelled out here so the browser bundle does not import the loop; a test
 * holds the two equal. A reprice with other options would undo the run's
 * clean subtotal (B3 piece 'round'), the single-item HBCF fold (B5 'fit') or
 * HBCF following the notes' total (B6 'detail') on the rows it moves.
 */
export const SERVER_PRICING_OPTIONS = Object.freeze({ foldStatutory: true, cleanSubtotal: true, foldIntoSingleItem: true, hbcfFollowsNotes: true });

const CLIENT_KINDS = new Set(['work', 'prelims', 'investigation']);
const round2 = (v: number) => Math.round((Number(v) || 0) * 100) / 100;
const amountOf = (v: unknown) => parseFloat(String(v ?? '0').replace(/,/g, '')) || 0;

/** The amount pricing-core gave this row when it was last priced. */
export function receiptAmount(row: Row): number | null {
    const r = row?.quoteBrain?.receipt;
    if (!r) return null;
    const printed = r.printedAmountIncGst ?? r.amountIncGst;
    return Number.isFinite(Number(printed)) ? Number(printed) : null;
}

/** True when the builder priced this brain row by hand. */
export function isBuilderPricedBrainRow(row: Row): boolean {
    if (row?.amountLocked === true || row?.amountEditedBy === 'user') return true;
    const receipt = receiptAmount(row);
    return receipt === null || Math.abs(amountOf(row?.amount) - receipt) > 0.005;
}

// The same fingerprint the server writes (backend write-project.js
// brainListFingerprint) cannot be recomputed here without node:crypto, so the
// browser compares the list's content directly with what the brain wrote:
// the row ids in order and each row still at its receipt amount, plus the
// count the server recorded.
function listIsAsWritten(scope: Row[], quoteData: Row | null): boolean {
    const meta = quoteData?.meta?.quoteBrain;
    if (!meta?.runId) return false;
    const brainRows = scope.filter((r) => r?.quoteBrain?.runId === meta.runId);
    if (brainRows.length !== scope.length) return false;
    const written = Array.isArray(meta.writtenRowIds) ? meta.writtenRowIds : null;
    if (written && (written.length !== scope.length || written.some((id: string, i: number) => scope[i]?.id !== id))) return false;
    return scope.every((r) => r?.quoteBrain?.kind === 'statutory' || !isBuilderPricedBrainRow(r));
}

export interface BrainRepriceResult {
    ok: boolean;
    reason?: string;
    changed: boolean;
    scope: Row[];
    moved: Array<{ index: number; title: string; from: number; to: number }>;
    checked: number;
}

// The fields that make a brain row's amount the builder's own (isBuilderPricedBrainRow).
const HAND_PRICE_KEYS = ['amountLocked', 'amountLockReason', 'amountEditedBy', 'amountEditedAt'];

/**
 * Run a brain reprice on the list as Stage 4 stores it. Stage 4 keeps each
 * amount on the quote's display basis, and the receipts and pricing-core are
 * inc GST. An ex-GST list is priced on its inc-GST view, and only the rows
 * the reprice wrote are converted back; every other row comes back as the
 * same object (the pattern of stage4-pricing.ts
 * reconcileStatutoryRowsOnScopeBasis). `moved` is on the list's basis.
 * Without this an ex-GST list read every row as hand-priced against its
 * inc receipt and was never repriced (money review N1, 2026-10-29).
 */
export function repriceOnScopeBasis(scope: Row[], isGstInclusive: boolean, price: (incRows: Row[]) => BrainRepriceResult): BrainRepriceResult {
    const rows = Array.isArray(scope) ? scope : [];
    if (isGstInclusive !== false) return price(rows);
    const incRows = convertScopePricingDisplayBasis(rows, { fromGstInclusive: false, toGstInclusive: true });
    const result = price(incRows);
    if (!result.ok || !result.changed) return { ...result, scope: rows, moved: [] };
    const out: Row[] = result.scope.map((r, i) => (
        r === incRows[i] ? rows[i] : convertScopePricingDisplayBasis([r], { fromGstInclusive: true, toGstInclusive: false })[0]
    ) as Row);
    return {
        ...result,
        scope: out,
        moved: result.moved.map((m) => ({ ...m, from: amountOf(rows[m.index]?.amount), to: amountOf(out[m.index]?.amount) })),
    };
}

/**
 * Price a brain project's rows again with pricing-core. Returns the rows to
 * store (the same array when nothing moved) and what moved.
 *
 * `force` names rows whose build-up the builder just edited (F2, lucid 018
 * O2, quote-brain-buildup.ts). Each is priced from its lines even when the
 * list still reads as written, and a price typed by hand is replaced (D2):
 * the row loses its hand-price fields and records `buildUpEditedAt`. The
 * other rows follow the usual rules, so `moved` names every price the change
 * moved (D5).
 *
 * `isGstInclusive` is the list's display basis (default inc GST); an ex-GST
 * list is priced through repriceOnScopeBasis.
 *
 * `method` is the Pricing page's method (quote-brain-buildup.ts
 * pricingPageMethodLayer). Only a quote QuoteMaker did not write uses it; a
 * QuoteMaker quote prices with the method its run recorded.
 */
export function repriceBrainScope(scope: Row[], quoteData: Row | null, { force = [], now = new Date().toISOString(), isGstInclusive = true, method: pageMethod = null }: { force?: Iterable<string>; now?: string; isGstInclusive?: boolean; method?: Row | null } = {}): BrainRepriceResult {
    if (isGstInclusive === false) return repriceOnScopeBasis(scope, false, (incRows) => repriceBrainScope(incRows, quoteData, { force, now, method: pageMethod }));
    const rows = Array.isArray(scope) ? scope : [];
    const forced = new Set([...force].map(String));
    const none = (reason?: string): BrainRepriceResult => ({ ok: !reason, ...(reason ? { reason } : {}), changed: false, scope: rows, moved: [], checked: 0 });
    if (!forced.size && listIsAsWritten(rows, quoteData)) return none();
    const meta = quoteData?.meta?.quoteBrain || {};
    const handQuote = !isQuoteBrainQuoteData({ ...(quoteData || {}), scope: rows });
    const method = handQuote
        ? (pageMethod && typeof pageMethod === 'object' ? pageMethod : null)
        : (meta.pricingMethodology && typeof meta.pricingMethodology === 'object' ? meta.pricingMethodology : null);

    const builtUp = rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => row?.quoteBrain?.itemId && CLIENT_KINDS.has(row.quoteBrain.kind) && Array.isArray(row.quoteBrain.buildUp));
    if (!builtUp.length) return none();
    if (handQuote && !method) return none('Your Pricing page has not loaded, has no rates yet, or needs Pricing access to price from, so no price was changed.');
    // Each row is priced under its own id with its own recorded rates: after
    // Adopt the list can hold rows of two runs, with item ids and rates of
    // their own.
    const keyOf = (row: Row) => String(row.id);
    // On a quote QuoteMaker did not write, an item whose price the builder
    // typed after pricing it from its lines counts at that price, as the job's
    // other items do, not at its lines' price.
    const brain = handQuote ? builtUp.filter(({ row }) => forced.has(keyOf(row)) || !isBuilderPricedBrainRow(row)) : builtUp;
    if (!brain.length) return none();
    const rateBook = method ? rateBookFromReceipts(brain.map(({ row }) => ({ key: keyOf(row), receipt: row.quoteBrain.receipt }))) : null;
    const buildUpOf = (row: Row) => row.quoteBrain.buildUp.map((l: Row) => (
        l?.source === 'rate' ? { ...l, rateId: receiptKey(keyOf(row), l.rateId) }
            : l?.source === 'labour' ? { ...l, trade: receiptKey(keyOf(row), l.trade) }
                : l
    ));
    const recorded = !!rateBook && brain.every(({ row }) => buildUpOf(row).every((l: Row) => (
        l?.source === 'rate' ? !!rateBook.get(l.rateId) : l?.source === 'labour' ? !!rateBook.dayRate(l.trade) : true
    )));
    if (!recorded) return none('This quote does not record the Pricing page rates QuoteMaker priced it with, so its prices were left as they are.');
    const items = brain.map(({ row }) => ({
        id: keyOf(row),
        kind: row.quoteBrain.kind,
        title: String(row.title || ''),
        summary: String(row.description || ''),
        lines: [],
        approvalGroup: row.quoteBrain.approvalGroup || 'project',
        evidenceRefs: row.quoteBrain.evidenceRefs || [],
        buildUp: buildUpOf(row),
    }));
    const M = pricingPageMethodology(method) as Row;
    const brainRows = new Set(brain.map(({ row }) => row));
    const isStatutoryRow = (row: Row) => row?.quoteBrain?.kind === 'statutory' || isManagedHbcfRow(row) || isManagedComplianceRow(row);
    if (handQuote) {
        // The job's other items, at their own prices, so the approval group is
        // the whole job. Each is one supplier-priced line sized so the band
        // gives back its price; pricing-core's result for them is never stored.
        const resolved = M.resolveRjBirdPricingMethodology(null);
        const band = Number(M.normalisePricingBand('medium', resolved)?.multiplier) || 1;
        const gst = Number(resolved?.gst_rate ?? resolved?.gstRate);
        const gstFactor = 1 + (Number.isFinite(gst) && gst >= 0 ? gst : 0.1);
        rows.forEach((row, index) => {
            const amountIncGst = amountOf(row?.amount);
            if (brainRows.has(row) || isStatutoryRow(row) || !(amountIncGst > 0)) return;
            items.push({
                id: `\u0001job-item\u0001${index}`,
                kind: 'work',
                title: String(row?.title || ''),
                summary: '',
                lines: [],
                approvalGroup: 'project',
                evidenceRefs: [],
                buildUp: [{ source: 'market', productName: String(row?.title || ''), qty: 1, sellExGst: amountIncGst / gstFactor / band }],
            });
        });
    }
    // The adapter is plain JS; its `items = []` default would type the list as never[].
    const priceItems = createPriceItems({ methodology: M, rateBook }) as unknown as (args: Record<string, any>) => any;
    const quoteFields = {
        ...(meta.compliance ? { compliance: meta.compliance } : {}),
        ...(meta.band ? { band: { value: meta.band, notesLine: meta.bandNotesLine || '' } } : {}),
    };
    // The statutory amounts the first run's clean subtotal counted: the
    // stored statutory rows and any premium a host item folded in. They are
    // fixed after the first run (memory feedback_hbcf_first_run_only), so a
    // reprice passes them as they stand; without them the clean subtotal
    // aimed at the items alone and moved untouched items on HBCF jobs (F2
    // money review, 2026-10-29).
    const fixedStatutoryIncGst = round2(rows.reduce((sum, row) => {
        if (brainRows.has(row)) return sum + (Number(row.quoteBrain.receipt?.statutoryIncGst) || 0);
        return isStatutoryRow(row) ? sum + amountOf(row.amount) : sum;
    }, 0));
    const priced = priceItems({ items, quoteFields, notesText: meta.bandNotesLine || '', firstRun: false, fixedStatutoryIncGst, ...SERVER_PRICING_OPTIONS, ...(handQuote ? { cleanSubtotal: false } : {}) });
    const byItem = new Map(priced.items.map((r: Row) => [r.itemId, r]));

    const moved: BrainRepriceResult['moved'] = [];
    const next = rows.slice();
    let written = 0;
    for (const { row, index } of brain) {
        const isForced = forced.has(keyOf(row));
        if (!isForced && isBuilderPricedBrainRow(row)) continue;
        const r = byItem.get(keyOf(row)) as Row | undefined;
        if (!r) continue;
        // A notes-priced Preliminaries item that printed a folded HBCF premium
        // keeps that premium: the statutory amounts are fixed after the first run.
        const folded = Number(row.quoteBrain.receipt?.statutoryIncGst) || 0;
        const to = round2(Number(r.amountIncGst) + folded);
        const from = amountOf(row.amount);
        const priceMoved = Math.abs(to - from) > 0.005;
        if (!priceMoved && !isForced) continue;
        const base: Row = isForced
            ? { ...Object.fromEntries(Object.entries(row).filter(([k]) => !HAND_PRICE_KEYS.includes(k))), buildUpEditedAt: now }
            : row;
        // A quoted-amount stamp the row carries (an imported row, or one edited
        // in the final quote) follows the new amount, so no reader takes the old one.
        next[index] = restampScopeItemPricingBasis({
            ...base,
            amount: String(to),
            quoteBrain: {
                ...row.quoteBrain,
                receipt: {
                    ...(row.quoteBrain.receipt || {}),
                    amountIncGst: r.amountIncGst,
                    ...(folded ? { printedAmountIncGst: to } : {}),
                    adminShareExGst: r.adminShareExGst || 0,
                    minimumAttendanceIncGst: r.minimumAttendanceIncGst || 0,
                    repricedAt: now,
                },
            },
        }, true);
        written += 1;
        if (priceMoved) moved.push({ index, title: String(row.title || `Item ${index + 1}`), from, to });
    }
    return { ok: true, changed: written > 0, scope: written ? next : rows, moved, checked: brain.length };
}
